import { useCallback, useEffect, useRef, useState } from 'react';
import { AgentService } from '@/services/agent/AgentService';
import { useProjectStore } from '@/stores/projectStore';
import { SkillRunState, SkillPendingAsk } from '@/types';

/**
 * useNodeSkillSession — 节点级技能执行会话（详情页画布节点接入智能体技能 §5.2）
 *
 * 画布详情页节点（planner/designer）通过本 hook 与后端技能工作流交互：
 * 1. 发起：start 调 AgentService.chatStream（透传 skill_id / stop_after_skill）；
 * 2. SSE 消费：onTaskStage（进度）、onTaskAsk（交互）、onChunk（文本累积）、
 *    onDone/onError（终态）、onSkillResult（P1-2 工作流终态 persist_rules 键值）；
 * 3. 自动应答：task_ask(mode=workflow) 按 autoReplyRules 匹配 workflowStep，
 *    命中立即 workflowReply（可携带 attachments），未命中暴露 pendingAsk；
 * 4. 交互：reply / cancel（workflowReply / workflowCancel）；
 * 5. 中止：abort（断开 SSE；后台流继续，凭 sessionId 可恢复）。
 *
 * SSE 生命周期（§5.2 注记，hook 设计关键前提）：收到 task_ask 后不关闭/abort/
 * 重连原流——后端协程挂起于 asyncio.Future 等待回复（WORKFLOW_REPLY_WAIT=300s
 * 超时），workflowReply 成功后 resolve_workflow_reply 解除 Future，续跑事件
 * 复用原 chunk_queue 从原流继续推送，onChunk/onTaskStage/onDone 原样续收——
 * 流在途不重连原流；仅连接失联（事件静默超阈值/流异常断开）时才降级为
 * 轮询接管自愈。若 workflowReply 返回 success:false，表示等待
 * 中的 Future 已不存在（超时/服务重启/已取消），原流不会再推进——置 error，
 * 恢复走 §5.8（凭持久化 wf_state + metadata.task_ask_pending 重新发起请求回到
 * 原步骤）。
 *
 * 状态分层：
 * - runState：持久化态（经 onRunStatePersist 写入 planner.runState /
 *   designer.runState，是 §5.8 刷新恢复的凭据来源）；
 * - streamText / stage：仅内存（流式文本高频更新不落节点数据，避免每个
 *   chunk 触发画布 store 写入与全节点 diff；面板从 hook 返回值直接渲染）。
 */

/** task_ask 蛇形原始载荷 → 驼峰归一化（与 useTaskStageCallback 同构，节点链路独立消费） */
export interface NormalizedTaskAsk {
  title: string;
  options: Array<{ id: string; label: string; index: number; description?: string; replyText?: string }>;
  allowTextInput: boolean;
  placeholder: string;
  currentStep: number;
  totalSteps: number;
  skillId: string;
  assistantMsgId: string;
  workflowSkill?: string;
  workflowStep?: string;
  allowUpload?: boolean;
  uploadAccept?: string;
  uploadMultiple?: boolean;
  uploadMaxCount?: number;
  uploadRoleText?: string;
  extraMetadata?: Record<string, any>;
}

/**
 * 自动应答规则：按 workflowStep 匹配 task_ask（§5.3 规则表的代码化载体，
 * 由调用方在 start 时注入，如 planner 注入 product_receiving/style_selection 规则）
 */
export interface NodeSkillAutoReplyRule {
  /** 匹配的 workflowStep 名（如 product_receiving / style_selection） */
  workflowStep: string;
  /** 命中后的回复文本；函数返回空值表示本次不自动应答（渲染交互卡） */
  reply?: string | ((ask: NormalizedTaskAsk) => string | null | undefined);
  /** 回复随附附件（如竞品图资产 payload；函数形式可按 ask 内容决定） */
  attachments?: Array<Record<string, any>> | ((ask: NormalizedTaskAsk) => Array<Record<string, any>> | null | undefined);
  /** 回复显示文本（后端存入 workflow_qa_pairs.answer） */
  displayText?: string;
}

export interface StartNodeSkillOptions {
  /** 首条消息随附附件（产品图资产 payload） */
  attachments?: any[];
  modelId?: string;
  /** 新执行不传（hook 生成）；刷新恢复/链式继承 designer 时传入 */
  sessionId?: string;
  autoReplyRules?: NodeSkillAutoReplyRule[];
  /** 工作流终态确认后阻断 handoff 自动续跑（节点链路语义，默认 true） */
  stopAfterSkill?: boolean;
  /** P1-2 skill_result 结构化事件透传（persist_rules 落库键值） */
  onSkillResult?: (data: { skill_id: string; session_id: string; results: Record<string, string> }) => void;
  /** 流正常完结（结果解析在此回调内通过 getStreamText 取全文） */
  onDone?: () => void;
  onError?: (error: string) => void;
}

export interface UseNodeSkillSessionOptions {
  /** runState 持久化回调（调用方写入 planner.runState / designer.runState） */
  onRunStatePersist?: (runState: SkillRunState) => void;
}

/** §5.8 在途恢复轮询：间隔 3s；上限 400 次（约 20 分钟，覆盖 WORKFLOW_REPLY_WAIT=300s 与长生成） */
const RESUME_POLL_INTERVAL_MS = 3000;
const RESUME_POLL_MAX_COUNT = 400;
/** unknown 态容忍次数（防任务刚创建的启动竞态误判为不可恢复） */
const RESUME_UNKNOWN_TOLERANCE = 3;
/** 运行停滞看门狗：后端心跳 3s/跳，任意流事件静默超过该阈值视为连接失联，降级轮询自愈 */
const STALL_WATCHDOG_TIMEOUT_MS = 45_000;
/** 看门狗巡检间隔 */
const STALL_WATCHDOG_CHECK_MS = 5_000;

/** §5.8 恢复选项：挂起重建后重放自动应答；完结恢复文本后触发结果解析（与 start 的 onDone 等价） */
export interface ResumeNodeSkillOptions {
  autoReplyRules?: NodeSkillAutoReplyRule[];
  onDone?: () => void;
  onError?: (error: string) => void;
}

/** 持久化 pendingAsk → 内存 ask 重建（刷新后 activeAskRef 丢失，回复需 assistantMsgId 清后端挂起标记） */
function pendingAskToNormalized(persisted: SkillRunState): NormalizedTaskAsk {
  const p = persisted.pendingAsk!;
  return {
    title: p.title,
    options: (p.options || []).map((o, idx) => ({
      id: o.id || String(idx),
      label: o.label,
      index: o.index ?? idx,
      description: o.description,
      replyText: o.replyText,
    })),
    allowTextInput: !!p.allowTextInput,
    placeholder: p.placeholder || '',
    currentStep: p.currentStep ?? 1,
    totalSteps: p.totalSteps ?? 1,
    skillId: p.skillId || '',
    assistantMsgId: persisted.assistantMsgId || '',
    workflowStep: persisted.currentStep,
    allowUpload: !!p.allowUpload,
    uploadAccept: 'image/*',
    uploadMultiple: false,
    uploadMaxCount: 5,
    uploadRoleText: '',
    extraMetadata: undefined,
  };
}

/** 执行过程面板的阶段行（task_stage 透出，仅内存不持久化） */
export interface NodeSkillStage {
  label: string;
  detail: string;
}

/** task_ask 蛇形原始载荷归一化（与 useTaskStageCallback 映射规则同源） */
export function normalizeTaskAsk(raw: any): NormalizedTaskAsk | null {
  if (!raw || typeof raw !== 'object') return null;
  // generation_confirm 模式不属于节点链路语义，忽略
  if (raw.generation_options) return null;
  const options = (Array.isArray(raw.options) ? raw.options : []).map((opt: any, idx: number) => ({
    id: opt?.id || String(idx),
    label: opt?.label || '',
    index: opt?.index ?? idx,
    description: opt?.description,
    replyText: opt?.reply_text,
  }));
  return {
    title: raw.title || '',
    options,
    allowTextInput: raw.allow_text_input ?? false,
    placeholder: raw.placeholder || '',
    currentStep: raw.current_step ?? 1,
    totalSteps: raw.total_steps ?? 1,
    skillId: raw.skill_id || '',
    assistantMsgId: raw.assistant_msg_id || '',
    workflowSkill: raw.workflow_skill || undefined,
    workflowStep: raw.workflow_step || undefined,
    allowUpload: raw.allow_upload ?? false,
    uploadAccept: raw.upload_accept || 'image/*',
    uploadMultiple: raw.upload_multiple ?? false,
    // 0 是后端 manifest 默认陷阱值，与未定义一并回退默认 5
    uploadMaxCount: raw.upload_max_count || 5,
    uploadRoleText: raw.upload_role_text || '',
    extraMetadata: raw.extra_metadata || undefined,
  };
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function useNodeSkillSession(sessionOptions: UseNodeSkillSessionOptions = {}) {
  const [runState, setRunState] = useState<SkillRunState>({ status: 'idle' });
  const [streamText, setStreamText] = useState('');
  const [stage, setStage] = useState<NodeSkillStage | null>(null);

  const controllerRef = useRef<AbortController | null>(null);
  const activeRef = useRef(false);
  const cancelledRef = useRef(false);
  const sessionIdRef = useRef('');
  const streamTextRef = useRef('');
  const activeAskRef = useRef<NormalizedTaskAsk | null>(null);
  const autoReplyRulesRef = useRef<NodeSkillAutoReplyRule[]>([]);
  const runStateRef = useRef<SkillRunState>({ status: 'idle' });
  const sessionOptionsRef = useRef(sessionOptions);
  sessionOptionsRef.current = sessionOptions;
  // §5.8 恢复：轮询定时器 / 恢复选项 / 「挂起回复提交成功后重新进入轮询接管」标志
  const resumeTimerRef = useRef<number | null>(null);
  const resumeOptsRef = useRef<ResumeNodeSkillOptions | null>(null);
  const adoptPollAfterReplyRef = useRef(false);
  // 在途流停滞看门狗：最近一次流事件时间戳 / 巡检定时器 / 失联降级回调
  const lastActivityRef = useRef(0);
  const watchdogTimerRef = useRef<number | null>(null);
  const watchdogStallRef = useRef<(() => void) | null>(null);
  // start（定义于 startResumePolling 之前）经此后取调用轮询接管
  const startResumePollingRef = useRef<(sessionId: string, assistantMsgId?: string) => void>(() => {});

  /** runState 单一更新口：ref 同步 + setState + 持久化回调 */
  const applyRunState = useCallback((patch: Partial<SkillRunState>) => {
    const next: SkillRunState = { ...runStateRef.current, ...patch };
    runStateRef.current = next;
    setRunState(next);
    sessionOptionsRef.current.onRunStatePersist?.(next);
  }, []);

  /** 看门狗停表 */
  const stopWatchdog = useCallback(() => {
    if (watchdogTimerRef.current !== null) {
      window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  }, []);

  /**
   * 在途流停滞看门狗：running 态下任意流事件（含 3s 心跳）刷新活跃度，
   * 静默超过 STALL_WATCHDOG_TIMEOUT_MS 判定连接失联——停表后触发 onStall
   * 降级轮询接管（§5.8）。waiting_ask（后端挂起等回复）不监控。
   */
  const startWatchdog = useCallback((onStall: () => void) => {
    watchdogStallRef.current = onStall;
    stopWatchdog();
    lastActivityRef.current = Date.now();
    watchdogTimerRef.current = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current < STALL_WATCHDOG_TIMEOUT_MS) return;
      stopWatchdog();
      if (!activeRef.current || runStateRef.current.status !== 'running') return;
      onStall();
    }, STALL_WATCHDOG_CHECK_MS);
  }, [stopWatchdog]);

  // 组件卸载时静默断开在途流与恢复轮询（后台流继续，凭 sessionId 可再恢复，§5.8）
  useEffect(() => () => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    if (watchdogTimerRef.current !== null) {
      window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    adoptPollAfterReplyRef.current = false;
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  /** workflowReply 统一提交（自动应答与手动 reply 共用） */
  const submitReply = useCallback(async (
    text: string,
    ask: NormalizedTaskAsk | null,
    attachments?: Array<Record<string, any>>,
    displayText?: string,
  ) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    // 自动应答的瞬间经过不渲染 waiting_ask（§5.6）：直接置 running
    applyRunState({ status: 'running', pendingAsk: undefined });
    activeAskRef.current = null;
    // 回复提交使会话回到 running：本地流仍在途则重启停滞看门狗（恢复轮询链路无从流，不重启）
    if (activeRef.current && watchdogStallRef.current) startWatchdog(watchdogStallRef.current);
    try {
      const resp = await AgentService.workflowReply(
        sessionId,
        text,
        ask?.assistantMsgId || undefined,
        attachments || [],
        [],
        displayText,
      );
      if (!resp.success) {
        // Future 已不存在（300s 超时/服务重启/已取消）：原流不会再推进（§5.2 注记）
        adoptPollAfterReplyRef.current = false;
        applyRunState({
          status: 'error',
          error: resp.message || '工作流回复失败（可能已超时），请重新执行',
          pendingAsk: undefined,
        });
      } else if (adoptPollAfterReplyRef.current) {
        // §5.8 恢复场景的回复提交成功：后端续推无 SSE 消费者，重新进入轮询接管直至完结
        adoptPollAfterReplyRef.current = false;
        const sid = sessionIdRef.current;
        if (sid) startResumePolling(sid, runStateRef.current.assistantMsgId);
      }
    } catch (e: any) {
      applyRunState({ status: 'error', error: e?.message || '网络异常，回复失败', pendingAsk: undefined });
    }
  }, [applyRunState, startWatchdog]);

  const handleTaskAsk = useCallback((rawData: any) => {
    const ask = normalizeTaskAsk(rawData);
    if (!ask) return;
    activeAskRef.current = ask;
    // 自动应答匹配：命中即回复，不渲染交互卡（§5.6「自动应答的瞬间经过不渲染」）
    const rule = autoReplyRulesRef.current.find((r) => r.workflowStep === ask.workflowStep);
    if (rule) {
      const replyText = typeof rule.reply === 'function' ? rule.reply(ask) : rule.reply;
      if (replyText) {
        const atts = typeof rule.attachments === 'function' ? rule.attachments(ask) : rule.attachments;
        void submitReply(replyText, ask, atts || [], rule.displayText);
        return;
      }
    }
    // 未命中 → 暴露交互卡（waiting_ask）；pendingAsk 随 runState 持久化（§5.8 刷新重建）
    const pending: SkillPendingAsk = {
      title: ask.title,
      options: ask.options.map((o) => ({
        id: o.id,
        label: o.label,
        index: o.index,
        description: o.description,
        replyText: o.replyText,
      })),
      allowTextInput: ask.allowTextInput || undefined,
      placeholder: ask.placeholder || undefined,
      allowUpload: ask.allowUpload || undefined,
      currentStep: ask.currentStep,
      totalSteps: ask.totalSteps,
      skillId: ask.skillId || undefined,
      assistantMsgId: ask.assistantMsgId || undefined,
    };
    applyRunState({
      status: 'waiting_ask',
      currentStep: ask.workflowStep,
      progress: ask.totalSteps > 0 ? ask.currentStep / ask.totalSteps : undefined,
      pendingAsk: pending,
    });
  }, [applyRunState, submitReply]);

  /** 发起技能执行（同一实例不并发两条流） */
  const start = useCallback((
    skillId: string,
    firstMessage: string,
    startOpts: StartNodeSkillOptions = {},
  ) => {
    if (activeRef.current) return;
    activeRef.current = true;
    cancelledRef.current = false;
    stopWatchdog();

    const sessionId = startOpts.sessionId || generateSessionId();
    sessionIdRef.current = sessionId;
    autoReplyRulesRef.current = startOpts.autoReplyRules || [];
    activeAskRef.current = null;
    streamTextRef.current = '';
    setStreamText('');
    setStage(null);
    applyRunState({
      status: 'running',
      sessionId,
      assistantMsgId: undefined,
      currentStep: undefined,
      progress: undefined,
      pendingAsk: undefined,
      error: undefined,
      completedAt: undefined,
    });

    // 停滞自愈（§5.8）：任一流事件刷新活跃度；静默超阈值判定连接失联——
    // 断开本地流并降级轮询接管（后端可能仍在跑或已完成，completed 时补拉全文完结）
    let sawAnyEvent = false;
    const touch = () => {
      sawAnyEvent = true;
      lastActivityRef.current = Date.now();
    };
    startWatchdog(() => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      resumeOptsRef.current = { onDone: startOpts.onDone, onError: startOpts.onError };
      controllerRef.current?.abort();
      controllerRef.current = null;
      setStage({ label: '连接中断', detail: '正在恢复任务状态…' });
      startResumePollingRef.current(sid, runStateRef.current.assistantMsgId);
    });

    // chatStream 异步返回 AbortController；resolve 后挂到 ref 供 cancel/abort 使用
    void AgentService.chatStream(
      [{ role: 'user', content: firstMessage }],
      (text) => {
        touch();
        streamTextRef.current += text;
        setStreamText(streamTextRef.current);
      },
      () => {
        touch();
        stopWatchdog();
        activeRef.current = false;
        if (cancelledRef.current) {
          // cancel 已本地收尾（后端取消关闭流触发的 done 不再覆盖状态）
          cancelledRef.current = false;
          return;
        }
        applyRunState({
          status: 'completed',
          completedAt: Date.now(),
          progress: 1,
          pendingAsk: undefined,
        });
        startOpts.onDone?.();
      },
      (error) => {
        touch();
        stopWatchdog();
        if (cancelledRef.current) {
          // cancel 已本地收尾：后端取消关闭流触发的 error 不再覆盖状态
          cancelledRef.current = false;
          activeRef.current = false;
          return;
        }
        // 请求级失败（后端未建任务，从未收到过任何流事件）直接报错；在途断流
        // （含静默断流）不立即置 error——后端任务可能实际已完成，降级轮询
        // 自愈（§5.8）：completed 补拉全文完结，failed/unknown 超限才报错
        const sid = sessionIdRef.current;
        if (!sawAnyEvent || !sid) {
          activeRef.current = false;
          applyRunState({ status: 'error', error, pendingAsk: undefined });
          startOpts.onError?.(error);
          return;
        }
        resumeOptsRef.current = { onDone: startOpts.onDone, onError: startOpts.onError };
        setStage({ label: '连接中断', detail: '正在恢复任务状态…' });
        startResumePollingRef.current(sid, runStateRef.current.assistantMsgId);
      },
      {
        sessionId,
        modelId: startOpts.modelId,
        skillId,
        attachments: startOpts.attachments,
        // 后端 agent_sessions.project_id 外键 NOT NULL：节点链路从 projectStore 取当前项目 id
        projectId: useProjectStore.getState().currentProjectId || undefined,
        stopAfterSkill: startOpts.stopAfterSkill ?? true,
        onHeartbeat: touch,
        onMessageIds: (ids) => {
          touch();
          if (ids.assistant_msg_id) applyRunState({ assistantMsgId: ids.assistant_msg_id });
        },
        onTaskStage: (data) => {
          touch();
          setStage({ label: data.label, detail: data.detail });
        },
        onTaskAsk: (raw) => {
          touch();
          handleTaskAsk(raw);
        },
        onSkillResult: startOpts.onSkillResult,
      },
    ).then((controller) => {
      controllerRef.current = controller;
    });
  }, [applyRunState, handleTaskAsk, startWatchdog, stopWatchdog]);

  /** 手动交互提交（交互卡选项/文本输入/上传），语义对齐 OptionConfirmSheet */
  const reply = useCallback((
    text: string,
    attachments?: Array<Record<string, any>>,
    displayText?: string,
  ) => {
    const ask = activeAskRef.current;
    const trimmed = text.trim();
    if (!trimmed && !(attachments && attachments.length)) return;
    // 只上传图片不输入文字时使用 uploadRoleText 作为 reply（方案 A 语义）
    const effective = trimmed || ask?.uploadRoleText || '我已上传图片，请继续';
    void submitReply(effective, ask, attachments, displayText || trimmed);
  }, [submitReply]);

  /** 取消：取消后端挂起 Future + 本地静默断开 + 停恢复轮询，状态回 idle（§5.6） */
  const cancel = useCallback(() => {
    const sessionId = sessionIdRef.current;
    cancelledRef.current = true;
    activeRef.current = false;
    activeAskRef.current = null;
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    adoptPollAfterReplyRef.current = false;
    if (sessionId) {
      void AgentService.workflowCancel(sessionId).catch(() => {});
    }
    stopWatchdog();
    controllerRef.current?.abort();
    controllerRef.current = null;
    applyRunState({ status: 'idle', pendingAsk: undefined });
  }, [applyRunState, stopWatchdog]);

  /** 中止：仅断开 SSE 与恢复轮询（后台流继续，凭 sessionId 可再恢复），状态回 idle（§5.6） */
  const abort = useCallback(() => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    adoptPollAfterReplyRef.current = false;
    stopWatchdog();
    controllerRef.current?.abort();
    controllerRef.current = null;
    activeRef.current = false;
    cancelledRef.current = false;
    activeAskRef.current = null;
    applyRunState({ status: 'idle', pendingAsk: undefined });
  }, [applyRunState, stopWatchdog]);

  /** 当前累积流全文（onDone 回调内取全文解析用，ref 直读避免闭包陈旧） */
  const getStreamText = useCallback(() => streamTextRef.current, []);

  // ─── §5.8 刷新/断线恢复 ─────────────────────────────────────────────

  /** 完结恢复：stream-status content 补拉全量（回退消息历史），灌回 streamText 后触发结果解析 */
  const finishResumeCompleted = useCallback(async (sessionId: string) => {
    const assistantMsgId = runStateRef.current.assistantMsgId;
    let text = '';
    try {
      const status = await AgentService.getStreamStatus(sessionId, assistantMsgId);
      text = (status.content || '').trim();
    } catch { /* content 不可得则回退消息历史 */ }
    if (!text) {
      try {
        const resp = await AgentService.getSessionMessages(sessionId, 50);
        const assistantMsgs = (resp.messages || []).filter((m) => m.role === 'assistant' && m.content);
        text = assistantMsgs[assistantMsgs.length - 1]?.content || '';
      } catch { /* 双通道均失败按空文本完结（结果解析对空文本天然跳过） */ }
    }
    // 会话已被用户取消/中止/重置（置 activeRef=false）则放弃灌回
    if (!activeRef.current || sessionIdRef.current !== sessionId) return;
    activeRef.current = false;
    streamTextRef.current = text;
    setStreamText(text);
    applyRunState({
      status: 'completed',
      completedAt: Date.now(),
      progress: 1,
      pendingAsk: undefined,
    });
    resumeOptsRef.current?.onDone?.();
  }, [applyRunState]);

  /** 恢复失败终态（failed / unknown 超限 / 轮询超时） */
  const finishResumeError = useCallback((message: string) => {
    if (!activeRef.current) return;
    activeRef.current = false;
    applyRunState({ status: 'error', error: message, pendingAsk: undefined });
    resumeOptsRef.current?.onError?.(message);
  }, [applyRunState]);

  /**
   * 轮询接管（§5.8 审查精确化）：RUNNING 态 stream-status 仅返回 state/taskId/contentLength
   * 不含流文本——故只轮询终态、不拼增量文本；完结后经 content 字段补拉全量。挂起态按
   * metadata.task_ask_pending 重建交互卡（含自动应答重放）。同 session 重新发起 chatStream
   * 会取消旧后台任务，故在途恢复只有轮询一条路。
   */
  const startResumePolling = useCallback((sessionId: string, assistantMsgId?: string) => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    let pollCount = 0;
    let unknownCount = 0;
    const schedule = () => {
      if (!activeRef.current || sessionIdRef.current !== sessionId) return;
      resumeTimerRef.current = window.setTimeout(() => { void tick(); }, RESUME_POLL_INTERVAL_MS);
    };
    const tick = async () => {
      resumeTimerRef.current = null;
      if (!activeRef.current || sessionIdRef.current !== sessionId) return;
      pollCount += 1;
      if (pollCount > RESUME_POLL_MAX_COUNT) {
        finishResumeError('恢复超时：后台任务长时间未完结，请重新执行');
        return;
      }
      let status: Awaited<ReturnType<typeof AgentService.getStreamStatus>>;
      try {
        status = await AgentService.getStreamStatus(sessionId, assistantMsgId);
      } catch {
        schedule(); // 网络抖动不终止恢复，按间隔重试直至次数上限
        return;
      }
      if (!activeRef.current || sessionIdRef.current !== sessionId) return;
      // 挂起（后端等回复）优先判定：按完整 ask_config 载荷重建交互卡（assistantMsgId 持久化兜底）
      const pendingRaw = status.metadata?.task_ask_pending;
      if (pendingRaw && typeof pendingRaw === 'object') {
        handleTaskAsk({
          ...pendingRaw,
          assistant_msg_id: pendingRaw.assistant_msg_id || assistantMsgId || '',
        });
        adoptPollAfterReplyRef.current = true;
        return;
      }
      // 后端已不挂起而本地仍 waiting_ask（刷新落在回复提交与持久化之间的窄窗口）：校正回 running
      if (runStateRef.current.status === 'waiting_ask' && status.state === 'running') {
        applyRunState({ status: 'running', pendingAsk: undefined });
      }
      if (status.state === 'completed') {
        await finishResumeCompleted(sessionId);
        return;
      }
      if (status.state === 'failed') {
        finishResumeError(status.error || '后台任务执行失败');
        return;
      }
      if (status.state === 'unknown') {
        unknownCount += 1;
        if (unknownCount >= RESUME_UNKNOWN_TOLERANCE) {
          finishResumeError('后台任务状态未知（服务可能已重启），请重新执行');
          return;
        }
      } else {
        unknownCount = 0;
      }
      schedule();
    };
    void tick();
  }, [finishResumeCompleted, finishResumeError, handleTaskAsk, applyRunState]);
  // 渲染期回填（同 sessionOptionsRef 惯例）：start 先于本定义，经 ref 后取；
  // 本函数全部依赖稳定，首渲染实例即终态，无陈旧闭包风险
  startResumePollingRef.current = startResumePolling;

  /**
   * 恢复入口（§5.8）：画布加载时由调用方以持久化 runState（status ∈ running/waiting_ask）
   * 调用。灌回持久化态后轮询 getStreamStatus：在途 → 持续轮询；挂起 → 重建交互卡（回复
   * 提交成功后重新接管轮询）；已完结 → 恢复 streamText 并触发结果解析。组件卸载自动停止
   * （后台流不受影响，可再次 resume）。
   */
  const resume = useCallback((persisted: SkillRunState, opts: ResumeNodeSkillOptions = {}) => {
    if (activeRef.current) return;
    const sessionId = persisted.sessionId;
    if (!sessionId) return;
    if (persisted.status !== 'running' && persisted.status !== 'waiting_ask') return;
    activeRef.current = true;
    cancelledRef.current = false;
    sessionIdRef.current = sessionId;
    autoReplyRulesRef.current = opts.autoReplyRules || [];
    resumeOptsRef.current = opts;
    activeAskRef.current = null;
    streamTextRef.current = '';
    setStreamText('');
    setStage(null);
    runStateRef.current = persisted;
    setRunState(persisted);
    // waiting_ask：先按持久化 pendingAsk 重建内存 ask，后端真实态由轮询首拍校正
    if (persisted.status === 'waiting_ask' && persisted.pendingAsk) {
      activeAskRef.current = pendingAskToNormalized(persisted);
    }
    startResumePolling(sessionId, persisted.assistantMsgId);
  }, [startResumePolling]);

  /** 清理本地会话态（不触后端、不写持久化；节点重置场景用） */
  const reset = useCallback(() => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    adoptPollAfterReplyRef.current = false;
    stopWatchdog();
    watchdogStallRef.current = null;
    resumeOptsRef.current = null;
    controllerRef.current?.abort();
    controllerRef.current = null;
    activeRef.current = false;
    cancelledRef.current = false;
    activeAskRef.current = null;
    sessionIdRef.current = '';
    autoReplyRulesRef.current = [];
    streamTextRef.current = '';
    setStreamText('');
    setStage(null);
    runStateRef.current = { status: 'idle' };
    setRunState({ status: 'idle' });
  }, [stopWatchdog]);

  return {
    runState,
    streamText,
    stage,
    start,
    reply,
    cancel,
    abort,
    resume,
    getStreamText,
    reset,
  };
}
